import styled from 'styled-components';

// Common styles and utilities
export const mobileFontSize = (desktopSize: string, tabletSize: string, mobileSize: string, smallMobileSize: string) => `
  font-size: ${desktopSize}; // Default for xl, lg
  @media (max-width: 991px) { // md
    font-size: ${tabletSize};
  }
  @media (max-width: 767px) { // sm
    font-size: ${mobileSize};
  }
  @media (max-width: 575px) { // xs
    font-size: ${smallMobileSize};
  }
`;

export const commonColors = {
  background: '#0B0B14',
  text: {
    primary: '#ffffff',
    secondary: '#94A3B8',
    muted: '#64748B'
  },
  border: 'rgba(255, 255, 255, 0.1)'
};

export const ButtonContainer = styled.div`
  display: flex;
  justify-content: center;
  gap: 16px;
  margin-bottom: 32px;

  @media (max-width: 768px) {
    flex-direction: column;
    align-items: center;
  }
`;

// New styled component for the Pricing Section
export const PricingSectionContainer = styled.section`
  padding: 120px 40px;
  margin: 0 auto;
  width: 80%;
  max-width: 1200px;

  @media (max-width: 768px) {
    padding: 40px 8px;
    width: 90%;
  }
`;

export const PricingGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 40px;
  width: 100%;
  margin-top: 80px;
  justify-items: center;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 24px;
    margin-top: 40px;
    padding: 0;
    justify-items: center;
  }
`;

// Styled Components
export const PageContainer = styled.div`
  background-color: ${commonColors.background};
  padding: 0 4px;
  color: ${commonColors.text.secondary};
  min-height: 100vh;
  width: 100%;
  max-width: 100%;
  margin: 0;
  overflow-x: hidden;
`;

export const StandardSection = styled.section`
  padding: 80px 20px;
  width: 80%;
  max-width: 1200px;
  margin: 0 auto;

  @media (max-width: 1199px) {
    padding: 80px 20px;
  }
  @media (max-width: 991px) {
    padding: 60px 16px;
  }
  @media (max-width: 767px) {
    padding: 40px 12px;
  }
  @media (max-width: 575px) {
    padding: 30px 8px;
  }
`;

export const TechStackContent = styled.div`
  background: rgba(255, 255, 255, 0.02);
  padding: 20px 10px;
  text-align: center;
  font-size: 1.1em;
  border-bottom: 1px solid ${commonColors.border};
  width: 100%;
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  align-items: center;
  gap: 15px;
  border-radius: 8px;

  @media (max-width: 767px) {
    font-size: 0.9em;
    width: 100%;
    padding: 15px 8px;
    gap: 10px;
  }
  @media (max-width: 575px) {
    padding: 12px 5px;
    gap: 8px;
  }
`;

export const TechStackItem = styled.span`
  color: ${commonColors.text.secondary};
  display: inline-flex;
  align-items: center;
  padding: 5px 8px;
  text-align: center;
  @media (max-width: 575px) { // xs
    flex-basis: 45%; // Example: try to fit two items per line, accounting for gap
    max-width: 150px; // Prevent items from getting too wide if they are alone on a line
  }
`;

export const HeroSection = styled(StandardSection)`
  text-align: center;
  width: 80%;
  border-bottom: 1px solid #222;
  padding-left: 0;
  padding-right: 0;
`;

export const HeroTitle = styled.h1`
  ${mobileFontSize('4em', '3.5em', '3em', '2.5em')}
  margin-bottom: 20px;
  font-weight: 800;
  color: ${commonColors.text.primary};
  letter-spacing: -0.5px;
`;

export const HeroDescription = styled.p`
  ${mobileFontSize('1.4em', '1.3em', '1.2em', '1.1em')}
  max-width: 700px;
  margin: 0 auto 30px;
  color: ${commonColors.text.secondary};
  padding: 20px 20px;
`;

export const FeaturesSection = styled(StandardSection)`
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 80%;
`;

export const SectionTitle = styled.h2`
  ${mobileFontSize('2.5em', '2.2em', '2em', '1.8em')}
  text-align: center;
  margin-bottom: 80px;
  color: ${commonColors.text.primary};
  letter-spacing: -0.5px;
  padding: 0 10px;

  @media (max-width: 768px) {
    margin-bottom: 40px;
    padding: 0 4px;
  }
`;

export const SectionSubtitle = styled.p`
  text-align: center;
  max-width: 600px;
  margin: 0 auto 40px auto;
  color: #94A3B8;
  font-size: 1.2em;
  padding: 0 8px;

  @media (max-width: 768px) {
    font-size: 1em;
    margin-bottom: 24px;
    padding: 0 4px;
  }
`;

export const FeaturesGrid = styled.div`
  display: grid;
  width: 100%;
  margin: 0 auto;
  justify-items: center;

  // Default for xl and up (e.g., 3 columns)
  grid-template-columns: repeat(3, 1fr);
  gap: 30px;
  padding: 0 20px;
  max-width: 1000px;

  @media (max-width: 1199px) { // lg (still 3 columns, maybe smaller gap/padding)
    gap: 24px;
    padding: 0 16px;
    max-width: 960px;
  }
  @media (max-width: 991px) { // md (2 columns)
    grid-template-columns: repeat(2, 1fr);
    gap: 20px;
    padding: 0 12px;
    max-width: 700px;
  }
  @media (max-width: 767px) { // sm (2 columns or 1, depending on card width)
    grid-template-columns: repeat(2, 1fr); // Could be 1fr if cards are wide
    gap: 16px;
    padding: 0 8px;
  }
  @media (max-width: 575px) { // xs (1 column)
    grid-template-columns: 1fr;
    gap: 16px;
    padding: 0 4px;
  }
`;


export const Footer = styled.footer`
  background-color: ${commonColors.background};
  color: ${commonColors.text.muted};
  text-align: center;
  padding: 30px;
  border-top: 1px solid ${commonColors.border};
  width: 100%;

  @media (max-width: 768px) {
    padding: 20px;
  }
`;

export const FooterText = styled.p`
  font-size: 1em;

  @media (max-width: 768px) {
    font-size: 0.9em;
  }
`;
